import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, signal } from '@angular/core';
import { NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';
import { v4 as uuid4 } from 'uuid';
import { Subject, takeUntil } from 'rxjs';
import { AssetListColor, AssetListStatus, MILESTONES } from '../../../layouts/my-projects/project.const';
import { CustomDateParserFormatter } from '../../services/datepicker-adapter';
import { EmitterService } from '../../services/emitter.service';
import { AssetListSortType, AssetListSortTypes } from '../../models';
import { AssetListStateService, AssetListSharedState } from './services/asset-list-state.service';
import {
  CampaignProjectDeletedEvent,
  CampaignProjectStatusUpdatedEvent,
  NewAssetAddedEvent,
  ProjectAssetLinkedEvent,
} from '../../models/events';
import { FunnelHeaderService } from './services/funnel-header.service';
import { AssetOperationsService } from './services/asset-operations.service';
import { AssetDataService } from './services/asset-data.service';
import { AssetSelectionService } from './services/asset-selection.service';
import { AssetListUiHelperService } from './services/ui-helper.service';
import { AssetPropertiesAvailabilityService } from './services/asset-properties-availability.service';
import { DropdownController, Asset } from './models/asset.model';
import { Project } from '../../interfaces';

@Component({
  selector: 'mtm-asset-list',
  templateUrl: './asset-list.component.html',
  styleUrl: './asset-list.component.scss',
  providers: [{ provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter }],
  standalone: false,
})
export class MtmAssetListComponent implements OnInit, OnChanges, OnDestroy {
  private readonly bundleSignal = signal<Asset[] | undefined>(undefined);
  private readonly originalBundleSignal = signal<Asset[] | undefined>(undefined);
  private readonly assetStarSignal = signal<string | undefined>(undefined);
  private readonly asSelectorSignal = signal<boolean>(false);
  private readonly multipleSignal = signal<boolean>(false);
  private readonly selectedAssetsSignal = signal<Asset[] | undefined>(undefined);
  private readonly workspaceIdSignal = signal<string | undefined>(undefined);
  private readonly campaignIdSignal = signal<string | undefined>(undefined);
  private readonly readOnlySignal = signal<boolean>(false);
  private readonly hideFilterSignal = signal<boolean>(false);
  private readonly showStatusSignal = signal<boolean>(false);
  private readonly onlyShowAssetWithStatusSignal = signal<boolean>(false);
  private readonly asWidgetSignal = signal<boolean>(false);
  private readonly projectIdSignal = signal<string | undefined>(undefined);
  private readonly projectSignal = signal<Project | undefined>(undefined);
  private readonly fromCampaignDashboardSignal = signal<boolean>(false);
  private readonly currentSortSignal = signal<AssetListSortTypes>(AssetListSortType.releaseDate);
  private readonly isProjectSettingSignal = signal<boolean>(false);
  private readonly isProjectDashboardSignal = signal<boolean>(false);
  private readonly loadingSignal = signal<boolean>(false);

  get bundle(): Asset[] | undefined {
    return this.bundleSignal();
  }
  @Input()
  set bundle(value: Asset[] | undefined) {
    this.bundleSignal.set(value);
  }

  get originalBundle(): Asset[] | undefined {
    return this.originalBundleSignal();
  }
  @Input()
  set originalBundle(value: Asset[] | undefined) {
    this.originalBundleSignal.set(value);
  }

  get assetStar(): string | undefined {
    return this.assetStarSignal();
  }
  @Input()
  set assetStar(value: string | undefined) {
    this.assetStarSignal.set(value);
  }

  get asSelector(): boolean {
    return this.asSelectorSignal();
  }
  @Input()
  set asSelector(value: boolean) {
    this.asSelectorSignal.set(!!value);
  }

  get multiple(): boolean {
    return this.multipleSignal();
  }
  @Input()
  set multiple(value: boolean) {
    this.multipleSignal.set(!!value);
  }

  get selectedAssets(): Asset[] | undefined {
    return this.selectedAssetsSignal();
  }
  @Input()
  set selectedAssets(value: Asset[] | undefined) {
    this.selectedAssetsSignal.set(value);
  }

  get workspaceId(): string | undefined {
    return this.workspaceIdSignal();
  }
  @Input()
  set workspaceId(value: string | undefined) {
    this.workspaceIdSignal.set(value);
  }

  get campaignId(): string | undefined {
    return this.campaignIdSignal();
  }
  @Input()
  set campaignId(value: string | undefined) {
    this.campaignIdSignal.set(value);
  }

  get readOnly(): boolean {
    return this.readOnlySignal();
  }
  @Input()
  set readOnly(value: boolean) {
    this.readOnlySignal.set(!!value);
  }

  get hideFilter(): boolean {
    return this.hideFilterSignal();
  }
  @Input()
  set hideFilter(value: boolean) {
    this.hideFilterSignal.set(!!value);
  }

  get showStatus(): boolean {
    return this.showStatusSignal();
  }
  @Input()
  set showStatus(value: boolean) {
    this.showStatusSignal.set(!!value);
  }

  get onlyShowAssetWithStatus(): boolean {
    return this.onlyShowAssetWithStatusSignal();
  }
  @Input()
  set onlyShowAssetWithStatus(value: boolean) {
    this.onlyShowAssetWithStatusSignal.set(!!value);
  }

  get asWidget(): boolean {
    return this.asWidgetSignal();
  }
  @Input()
  set asWidget(value: boolean) {
    this.asWidgetSignal.set(!!value);
  }

  get projectId(): string | undefined {
    return this.projectIdSignal();
  }
  @Input()
  set projectId(value: string | undefined) {
    this.projectIdSignal.set(value);
  }

  get project(): Project | undefined {
    return this.projectSignal();
  }
  @Input()
  set project(value: Project | undefined) {
    this.projectSignal.set(value);
  }

  get fromCampaignDashboard(): boolean {
    return this.fromCampaignDashboardSignal();
  }
  @Input()
  set fromCampaignDashboard(value: boolean) {
    this.fromCampaignDashboardSignal.set(!!value);
  }

  get currentSort(): AssetListSortTypes {
    return this.currentSortSignal();
  }
  @Input()
  set currentSort(value: AssetListSortTypes) {
    this.currentSortSignal.set(value ?? AssetListSortType.releaseDate);
  }

  get isProjectSetting(): boolean {
    return this.isProjectSettingSignal();
  }
  @Input()
  set isProjectSetting(value: boolean) {
    this.isProjectSettingSignal.set(!!value);
  }

  get isProjectDashboard(): boolean {
    return this.isProjectDashboardSignal();
  }
  @Input()
  set isProjectDashboard(value: boolean) {
    this.isProjectDashboardSignal.set(!!value);
  }

  get loading(): boolean {
    return this.loadingSignal();
  }
  @Input()
  set loading(value: boolean) {
    this.loadingSignal.set(!!value);
  }
  @Output() assetSelected: EventEmitter<Asset[]> = new EventEmitter();
  @Output() updateTimelineAndProject: EventEmitter<{ allBundle: Asset[]; newAsset?: Asset }> = new EventEmitter();
  @Output() assetUpdated: EventEmitter<Asset> = new EventEmitter();
  @Output() assetDeleted: EventEmitter<Asset> = new EventEmitter();
  @Output() assetDeletedSubAsset: EventEmitter<Asset> = new EventEmitter();
  @Output() bulkUpdateAssets: EventEmitter<Asset[]> = new EventEmitter();
  @Output() dragAndDropAssets: EventEmitter<Asset[]> = new EventEmitter();
  @Output() assetBundleChange: EventEmitter<Asset[]> = new EventEmitter();

  @Output() filterVideoAndStill: EventEmitter<{ videoAndStill: string }> = new EventEmitter();

  state: AssetListSharedState;
  projectColor: typeof AssetListColor = AssetListColor;
  projectStatus: typeof AssetListStatus = AssetListStatus;
  ngUnsubscribe: Subject<void> = new Subject<void>();
  milestones: string[] = MILESTONES;

  constructor(
    private readonly stateService: AssetListStateService,
    private readonly funnelHeaderService: FunnelHeaderService,
    private readonly assetOperationsService: AssetOperationsService,
    private readonly assetDataService: AssetDataService,
    private readonly assetSelectionService: AssetSelectionService,
    private readonly assetListUiHelperService: AssetListUiHelperService,
    private readonly assetPropertiesAvailabilityService: AssetPropertiesAvailabilityService
  ) {
    this.state = this.stateService.currentState;

    EmitterService.get(NewAssetAddedEvent)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (result: Asset) => {
          if (!result) {
            return;
          }
          result.id = uuid4();
          result.assetBundle = this.assetStar;
          if (!this.originalBundle?.length) {
            return;
          }
          const bundle: Asset[] = [...this.originalBundle, result];
          this.stateService.updateState({
            allBundle: [...this.state.allBundle, result],
            allAssets: structuredClone(bundle),
          });
          this.assetUpdated.emit(result);
          if (this.projectId && this.asWidget) {
            this.updateTimelineAndProject.emit({ allBundle: this.state.allBundle, newAsset: result });
          }
        },
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.bundle?.currentValue) {
      this.stateService.updateState({
        allAssets: structuredClone(this.originalBundle || []),
      });
      if (this.projectId && this.asWidget) {
        this.stateService.updateState({
          allBundle: structuredClone(this.originalBundle || []),
        });
      }
      this.sortAssetBundle();
      this.initializeBundle();
    }
    if (changes.selectedAssets?.currentValue?.length) {
      this.stateService.updateState({
        selectedAssetTypes: this.selectedAssets.map((a: Asset) => a.assetListId),
      });
    }
    if (changes.currentSort?.currentValue) {
      this.sortAssetBundle();
    }
    if (this.campaignId) {
      this.getLinkedAssets();
      this.getCampaignProjects();
    }
  }

  ngOnInit(): void {
    this.stateService.state$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((state: AssetListSharedState) => {
      this.state = state;
    });

    this.assetPropertiesAvailabilityService
      .getAvailablePropertiesFromApi()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (properties: any) => {
          this.stateService.updateState({ funnelList: properties.funnel });
        },
      });

    this.stateService.updateState({ isEditing: !this.isProjectSetting });
    if (this.showStatus || this.onlyShowAssetWithStatus || this.asSelector) {
      this.getLinkedAssets();
      this.getCampaignProjects();
      EmitterService.get(CampaignProjectStatusUpdatedEvent)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe({
          next: () => {
            this.getLinkedAssets();
            this.getCampaignProjects();
          },
        });
      EmitterService.get(ProjectAssetLinkedEvent)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe({
          next: () => {
            this.getLinkedAssets();
            this.getCampaignProjects();
          },
        });
      EmitterService.get(CampaignProjectDeletedEvent)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe({
          next: () => {
            this.getLinkedAssets();
            this.getCampaignProjects();
          },
        });
    }
  }

  ngOnDestroy(): void {
    this.stateService.resetComponentState();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public initializeBundle(): void {
    this.bundle = this.assetListUiHelperService.initializeBundle(this.bundle || []);
  }

  public validateUUID(id: string): boolean {
    return this.assetListUiHelperService.validateUUID(id);
  }

  public getLengthLabel(val: string): string {
    return this.assetListUiHelperService.getLengthLabel(val);
  }

  public getCampaignProjects(): void {
    this.assetDataService.getCampaignProjects(this.campaignId, this.ngUnsubscribe);
  }

  public getAllAsset(): Asset[] {
    return this.assetDataService.getAllAsset();
  }

  public getLinkedAssets(): void {
    this.assetDataService.getLinkedAssets(
      this.campaignId,
      this.projectId,
      this.asWidget,
      this.onlyShowAssetWithStatus,
      () => this.filterBundleWithStatus(),
      this.ngUnsubscribe
    );
  }

  public sortAssetBundle(): void {
    this.bundle = this.assetDataService.sortAssetBundle(this.bundle || [], this.currentSort);
  }

  public filterBundleWithStatus(): void {
    this.bundle = this.assetDataService.filterBundleWithStatus(this.bundle || []);
  }

  public onMilestoneSelect(milestone: string): void {
    this.assetListUiHelperService.onMilestoneSelect(milestone, () => this.filterAssets());
  }

  public filterAssets(): void {
    this.assetListUiHelperService.filterAssets((data: { videoAndStill: string }) =>
      this.filterVideoAndStill.emit(data)
    );
  }

  public addNewAsset(funnel: string): void {
    this.assetOperationsService
      .addNewAsset(
        funnel,
        this.assetStar,
        this.bundle,
        this.originalBundle,
        this.projectId,
        this.asWidget,
        (asset: Asset) => this.assetUpdated.emit(asset),
        (bundle: Asset[]) => this.assetBundleChange.emit(bundle),
        (data: { allBundle: Asset[]; newAsset?: Asset }) => this.updateTimelineAndProject.emit(data)
      )
      ?.then((result: { newBundle: Asset[]; newOriginalBundle: Asset[] }) => {
        if (result) {
          this.bundle = result.newBundle;
          this.originalBundle = result.newOriginalBundle;
        }
      });
  }

  public onCancel(): void {
    this.assetSelectionService.onCancel();
  }

  public onSave(): void {
    this.assetSelectionService.onSave((selectedAssetTypes: Asset[]) => this.assetSelected.emit(selectedAssetTypes));
  }

  public toggleEdit(): void {
    this.assetSelectionService.toggleEdit();
  }

  public addAsset(): void {
    this.assetOperationsService.addAsset();
  }

  public over(drop: DropdownController): void {
    this.assetListUiHelperService.over(drop);
  }

  public out(drop: DropdownController): void {
    this.assetListUiHelperService.out(drop);
  }

  public hasHiddenAssets(category: string): boolean {
    return this.assetListUiHelperService.hasHiddenAssets(category, (cat: string) => this.getGroupedAsset(cat));
  }

  public getHiddenAssetsCount(category: string): number {
    return this.assetListUiHelperService.getHiddenAssetsCount(category, (cat: string) => this.getGroupedAsset(cat));
  }

  public toggleHiddenAssets(category: string): void {
    this.assetListUiHelperService.toggleHiddenAssets(category);
  }

  public returnToProject(): void {
    this.assetListUiHelperService.returnToProject();
  }

  public getGroupedAsset(funnel: string): Asset[] {
    return this.funnelHeaderService.getGroupedAsset(funnel, this.bundle);
  }

  public handleBundleChange(result: { newBundle: Asset[]; newOriginalBundle: Asset[] }): void {
    if (result) {
      this.bundle = result.newBundle;
      this.originalBundle = result.newOriginalBundle;
      this.assetBundleChange.emit(this.bundle);
    }
  }
}
